import { Navigate, Outlet, useLocation } from 'react-router';
import { UniversalLoadingState } from '@sighthost/ui';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { isStaffUser, isSupportUser } from './defaultRouteForUser';

/**
 * Route guard for the customer portal (`/app/*`).
 *
 * - Anonymous users → redirected to `/sign-in`, EXCEPT for the public-paths
 *   allow-list (LD-O6): the order-hosting wizard is reachable while signed
 *   out so marketing CTAs can drive Steps 1+2 before the auth gate fires
 *   at Step 3 submit.
 * - Staff users → redirected to `/admin` (staff don't belong in the customer
 *   portal; see `defaultRouteForUser`).
 * - Customers → admitted.
 *
 * Wrap customer route trees in App.tsx:
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/app/*" element={<ClientRouter />} />
 *   </Route>
 */

/**
 * Routes inside `/app/*` that may render unauthenticated. Page-level guards
 * (the order-hosting wizard's Step 3 submit) take over the auth gating for
 * these. Match by exact path or path-prefix (with trailing slash).
 */
const PUBLIC_APP_PATHS: readonly RegExp[] = [
  /^\/app\/services\/new(?:[/?#]|$)/,  // LD-O6 — order funnel Steps 1+2
];

function isPublicAppPath(pathname: string): boolean {
  return PUBLIC_APP_PATHS.some((re) => re.test(pathname));
}

export function ProtectedRoute() {
  const { loading, isAuthenticated, user } = useAuth();
  const { loading: roleLoading, isSalesStaff } = useStaffRole();
  const location = useLocation();

  if (loading || roleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-card-subtle">
        <UniversalLoadingState variant="spinner" caption="Loading…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (isPublicAppPath(location.pathname)) {
      return <Outlet />;
    }
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Staff don't belong in the customer portal.
  if (isStaffUser(user)) {
    return <Navigate to="/admin" replace />;
  }
  // Support agents belong in the (scoped) admin console, not /app.
  if (isSupportUser(user)) {
    return <Navigate to="/admin/customers" replace />;
  }
  // INCIDENT 2026-08-03: external sales staff were being ADMITTED here.
  // This guard only ever tested app_metadata.role, but Phase 11 sales staff
  // deliberately carry none (SP-18) — they are identified by staff_profile.
  // Earlier agents were masked by also holding `support`; the first agent
  // onboarded without it landed in the customer portal on first sign-in.
  // (Individuals are not named: this file ships to browsers and vite emits
  // public source maps, so comments are fetchable from the live site.)
  if (isSalesStaff) {
    return <Navigate to="/sales" replace />;
  }

  return <Outlet />;
}
